export type PdlReviewPathTourStep = {
  id: string;
  target: string;
  title: string;
  description: string;
  points: string[];
  tip?: string;
  tipPoints?: string[];
};

export const PDL_TOUR_TARGETS = {
  panel: 'pdl-decision-panel',
  brief: 'pdl-adjudicator-brief',
  waterfall: 'pdl-capacity-waterfall',
  fullBreakdown: 'pdl-eligibility-help-button',
  evidence: 'pdl-decision-evidence',
  technical: 'application-decision-engine-pdl-technical',
  policy: 'application-decision-engine-pdl-remarks',
  takeTour: 'pdl-decision-take-tour',
} as const;

/** Spotlight tour — how to read the PDL Decision Engine tab (mirrors employer review path). */
export function buildPdlDecisionReviewPathSteps(options?: {
  hasEvidence?: boolean;
}): PdlReviewPathTourStep[] {
  const hasEvidence = options?.hasEvidence ?? false;

  const steps: PdlReviewPathTourStep[] = [
    {
      id: 'decision-panel',
      target: PDL_TOUR_TARGETS.panel,
      title: 'Decision Engine tab',
      description: 'Payday loan capacity after verified bank income — no bureau pull for this path.',
      points: [
        'Work top to bottom: offer summary, then optional audit sections.',
        'Approve / Refer / Decline is shown on the summary card.',
        'Use Full breakdown for legacy scoring-table math.',
      ],
      tip: 'Scroll is locked during the tour so each highlight stays on the right section.',
    },
    {
      id: 'offer-summary',
      target: PDL_TOUR_TARGETS.brief,
      title: 'Offer summary',
      description: 'Start here for the decision and customer-facing amounts.',
      points: [
        'Compare what the customer requested vs what we can offer today.',
        'The waterfall below shows which limit capped the approved amount.',
        'Expand Evidence when you need income streams or ELOD debt detail.',
      ],
    },
    {
      id: 'capacity-waterfall',
      target: PDL_TOUR_TARGETS.waterfall,
      title: 'What capped the amount',
      description: 'Visual flow from request → pre-approval table → final offer.',
      points: [
        'Pre-approval limit comes from the legacy Cash4You scoring grid.',
        'The highlighted bar is the binding limit for this application.',
        'Bank affordability is optional — expand only when you need to compare.',
      ],
    },
    {
      id: 'full-breakdown',
      target: PDL_TOUR_TARGETS.fullBreakdown,
      title: 'Full breakdown',
      description: 'Step-by-step legacy grid calculation (table × band × tier).',
      points: [
        'Opens a dialog with income × loan % and cell maximum.',
        'No bank PTI/TDS math — legacy table only.',
        'Use when a reviewer asks “how did we get $500?”',
      ],
    },
  ];

  if (hasEvidence) {
    steps.push({
      id: 'evidence',
      target: PDL_TOUR_TARGETS.evidence,
      title: 'Supporting evidence',
      description: 'Bank income streams and existing debt signals used by the engine.',
      points: [
        'Expand to see verified income streams from the Income tab.',
        'ELOD shows monthly loan payments detected on the bank feed.',
        'Informational notes do not change the auto-decision by themselves.',
      ],
    });
  }

  steps.push(
    {
      id: 'technical-audit',
      target: PDL_TOUR_TARGETS.technical,
      title: 'Technical details',
      description: 'Full trace for audit — skip for routine review.',
      points: [
        'Scoring profile, pre-approval table steps, and final offer math.',
        'Bank payment-room steps are collapsed under audit-only.',
        'Amounts are formatted in dollars — use this for disputes.',
      ],
      tip: 'This section expands automatically during the tour.',
    },
    {
      id: 'policy-signals',
      target: PDL_TOUR_TARGETS.policy,
      title: 'Policy signals & factors',
      description: 'Regulatory and cost-of-living notes plus capacity context.',
      points: [
        '“Note only” items do not change the offer.',
        '“Set the offer limit” shows which rule bound the amount.',
        'Federal caps and MBM residual checks live here.',
      ],
      tip: 'This section expands automatically during the tour.',
    },
  );

  return steps;
}
